import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { cardsService } from '../services/cardsService';
import { Save, ArrowLeft, Upload, User, Linkedin, X, Building2, Sparkles, Star } from 'lucide-react';

const CardForm = () => {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const isEdit = !!cardId;
  const photoInputRef = useRef(null);
  const logoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: '',
    job_title: '',
    company: 'ATOM Group SA',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    bio: '',
    photo_base64: '',
    logo_base64: '', // Custom logo for franchises
    brand_color: '#3B9EFF',
    template: 'atom-glass',
    social_links: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: '',
      website: '',
    },
    is_public: true,
    google_review_url: 'https://maps.app.goo.gl/AAKh6XacvsUfuiKh8', // Default ATOM Google Reviews
    website_url: 'https://www.atom-group.ch', // Default ATOM website
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [useCustomLogo, setUseCustomLogo] = useState(false);
  const [useCustomGoogleReview, setUseCustomGoogleReview] = useState(false);
  const [useCustomWebsite, setUseCustomWebsite] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchCard();
    }
  }, [cardId]);

  const fetchCard = async () => {
    try {
      const card = await cardsService.getCard(cardId);
      setFormData({
        full_name: card.full_name || '',
        job_title: card.job_title || '',
        company: card.company || 'ATOM Group SA',
        email: card.email || '',
        phone: card.phone || '',
        mobile: card.mobile || '',
        address: card.address || '',
        bio: card.bio || '',
        photo_base64: card.photo_base64 || '',
        logo_base64: card.logo_base64 || '',
        brand_color: card.brand_color || '#3B9EFF',
        template: card.template || 'atom-glass',
        social_links: card.social_links || {
          linkedin: '',
          twitter: '',
          facebook: '',
          instagram: '',
          website: '',
        },
        is_public: card.is_public !== undefined ? card.is_public : true,
        google_review_url: card.google_review_url || 'https://maps.app.goo.gl/AAKh6XacvsUfuiKh8',
        website_url: card.website_url || 'https://www.atom-group.ch',
      });
      if (card.photo_base64) {
        setPhotoPreview(card.photo_base64);
      }
      if (card.logo_base64) {
        setLogoPreview(card.logo_base64);
        setUseCustomLogo(true);
      }
      // Check if custom Google Review URL
      if (card.google_review_url && card.google_review_url !== 'https://maps.app.goo.gl/AAKh6XacvsUfuiKh8') {
        setUseCustomGoogleReview(true);
      }
      // Check if custom website URL
      if (card.website_url && card.website_url !== 'https://www.atom-group.ch') {
        setUseCustomWebsite(true);
      }
    } catch (err) {
      setError('Erreur lors du chargement de la carte');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La photo ne doit pas dépasser 5 MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setPhotoPreview(base64);
        setFormData({ ...formData, photo_base64: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Le logo ne doit pas dépasser 2 MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setLogoPreview(base64);
        setFormData({ ...formData, logo_base64: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData({ ...formData, photo_base64: '' });
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData({ ...formData, logo_base64: '' });
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // If not using custom logo, clear it so default ATOM logo is used
      const dataToSubmit = { ...formData };
      if (!useCustomLogo) {
        dataToSubmit.logo_base64 = '';
      }
      
      if (isEdit) {
        await cardsService.updateCard(cardId, dataToSubmit);
      } else {
        await cardsService.createCard(dataToSubmit);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('social_')) {
      const socialField = name.replace('social_', '');
      setFormData({
        ...formData,
        social_links: {
          ...formData.social_links,
          [socialField]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#3B9EFF] to-cyan-400 rounded-xl flex items-center justify-center">
              <Sparkles size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-white text-3xl font-bold">
                {isEdit ? 'Modifier la carte' : 'Nouvelle carte'}
              </h1>
              <p className="text-gray-400">Carte de visite digitale ATOM Glass</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Photo & Identity Section */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-6 flex items-center space-x-2">
              <User size={20} className="text-[#3B9EFF]" />
              <span>Identité & Photo</span>
            </h2>
            
            <div className="flex flex-col md:flex-row gap-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center">
                <div className="relative group">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-32 h-32 rounded-full object-cover border-2 border-[#3B9EFF]/50"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => photoInputRef.current?.click()}
                      className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-dashed border-gray-600 hover:border-[#3B9EFF] flex flex-col items-center justify-center cursor-pointer transition-all group-hover:bg-gray-800"
                    >
                      <Upload size={24} className="text-gray-500 group-hover:text-[#3B9EFF] mb-1" />
                      <span className="text-xs text-gray-500 group-hover:text-[#3B9EFF]">Photo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <p className="text-gray-500 text-xs mt-2">Max 5 MB</p>
              </div>
              
              {/* Name & Title */}
              <div className="flex-1 space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                    placeholder="Joël Ott"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Titre / Poste *
                  </label>
                  <input
                    type="text"
                    name="job_title"
                    value={formData.job_title}
                    onChange={handleChange}
                    className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                    placeholder="Co-Fondateur & Directeur"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Entreprise *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                    placeholder="ATOM Group SA"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Logo Section - For Franchises */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-4 flex items-center space-x-2">
              <Building2 size={20} className="text-[#3B9EFF]" />
              <span>Logo de l'entreprise</span>
            </h2>
            
            <p className="text-gray-400 text-sm mb-4">
              Par défaut, le logo ATOM Group sera utilisé. Pour les franchises, vous pouvez uploader un logo personnalisé.
            </p>
            
            {/* Toggle for custom logo */}
            <div className="flex items-center justify-between mb-4 p-4 bg-white/[0.02] rounded-xl border border-white/5">
              <div>
                <p className="text-white font-medium">Utiliser un logo personnalisé</p>
                <p className="text-gray-500 text-xs">Pour les franchises et partenaires</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomLogo}
                  onChange={(e) => {
                    setUseCustomLogo(e.target.checked);
                    if (!e.target.checked) {
                      removeLogo();
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#3B9EFF]"></div>
              </label>
            </div>
            
            {/* Logo Upload - Only shown if custom logo is enabled */}
            {useCustomLogo && (
              <div className="flex items-center space-x-4">
                {logoPreview ? (
                  <div className="relative">
                    <div className="bg-white rounded-xl px-4 py-3 shadow-lg">
                      <img 
                        src={logoPreview} 
                        alt="Logo" 
                        className="h-10 w-auto"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center space-x-3 px-6 py-4 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-dashed border-gray-600 hover:border-[#3B9EFF] rounded-xl cursor-pointer transition-all"
                  >
                    <Upload size={20} className="text-gray-500" />
                    <span className="text-gray-400 text-sm">Uploader le logo (PNG, max 2MB)</span>
                  </div>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            )}
            
            {useCustomLogo && (
              <p className="text-gray-500 text-xs mt-3">
                💡 La mention "Powered by ATOM Group SA" apparaîtra en bas de la carte.
              </p>
            )}

            {/* Brand Color Picker */}
            <div className="mt-6 pt-6 border-t border-gray-800/50">
              <label className="block text-gray-300 text-sm font-medium mb-3">
                Couleur de marque
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="color"
                  name="brand_color"
                  value={formData.brand_color}
                  onChange={handleChange}
                  className="w-12 h-12 rounded-xl border-2 border-gray-700 cursor-pointer bg-transparent p-1 hover:border-[#3B9EFF] transition-all"
                />
                <span className="text-gray-400 text-sm font-mono bg-black/50 border border-gray-700 rounded-lg px-3 py-2">
                  {formData.brand_color.toUpperCase()}
                </span>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Cette couleur sera utilisée comme accent sur votre carte de visite.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-6">Coordonnées</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Email professionnel *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                  placeholder="joel@atom-group.ch"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Téléphone mobile
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                  placeholder="+41 79 123 45 67"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Téléphone fixe
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                  placeholder="+41 32 539 16 27"
                />
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                  placeholder="Rue de la Pierre-à-Mazel 39, 2000 Neuchâtel"
                />
              </div>
            </div>
          </div>

          {/* Bio Section */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-4">Présentation (optionnel)</h2>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows={3}
              className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all resize-none"
              placeholder="Conseiller financier spécialisé en optimisation fiscale et placements..."
            />
          </div>

          {/* LinkedIn Section */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-4 flex items-center space-x-2">
              <Linkedin size={20} className="text-[#0077B5]" />
              <span>LinkedIn</span>
            </h2>
            <input
              type="url"
              name="social_linkedin"
              value={formData.social_links.linkedin}
              onChange={handleChange}
              className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#0077B5] focus:ring-1 focus:ring-[#0077B5]/50 transition-all"
              placeholder="https://linkedin.com/in/joel-ott"
            />
            <p className="text-gray-500 text-xs mt-2">Le bouton LinkedIn apparaîtra sur votre carte</p>
          </div>

          {/* Website Section */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-cyan-500/20 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-4 flex items-center space-x-2">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span>Site Web</span>
            </h2>
            
            <p className="text-gray-400 text-sm mb-4">
              Par défaut, le site atom-group.ch sera affiché. Pour les franchises, personnalisez le lien.
            </p>
            
            {/* Toggle for custom website */}
            <div className="flex items-center justify-between mb-4 p-4 bg-white/[0.02] rounded-xl border border-white/5">
              <div>
                <p className="text-white font-medium">Utiliser un site web personnalisé</p>
                <p className="text-gray-500 text-xs">Pour les franchises avec leur propre site</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomWebsite}
                  onChange={(e) => {
                    setUseCustomWebsite(e.target.checked);
                    if (!e.target.checked) {
                      setFormData({ ...formData, website_url: 'https://www.atom-group.ch' });
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-cyan-500"></div>
              </label>
            </div>
            
            {/* Custom website URL input */}
            {useCustomWebsite && (
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  URL du site web
                </label>
                <input
                  type="url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-cyan-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                  placeholder="https://www.votre-site.ch"
                />
              </div>
            )}
          </div>

          {/* Google Reviews Section */}
          <div className="bg-gradient-to-br from-[#1a1815] to-[#111] border border-yellow-500/20 rounded-2xl p-6">
            <h2 className="text-white text-lg font-semibold mb-4 flex items-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Avis Google</span>
              <span className="ml-2 flex items-center space-x-0.5">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </span>
            </h2>
            
            <p className="text-gray-400 text-sm mb-4">
              Un bouton incitera vos clients à laisser un avis Google. Par défaut, le lien ATOM Group est utilisé.
            </p>
            
            {/* Toggle for custom Google Review URL */}
            <div className="flex items-center justify-between mb-4 p-4 bg-white/[0.02] rounded-xl border border-white/5">
              <div>
                <p className="text-white font-medium">Utiliser un lien Google personnalisé</p>
                <p className="text-gray-500 text-xs">Pour les franchises avec leur propre page Google</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomGoogleReview}
                  onChange={(e) => {
                    setUseCustomGoogleReview(e.target.checked);
                    if (!e.target.checked) {
                      setFormData({ ...formData, google_review_url: 'https://maps.app.goo.gl/AAKh6XacvsUfuiKh8' });
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>
            
            {/* Custom Google Review URL input */}
            {useCustomGoogleReview && (
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Lien Google Reviews de votre entreprise
                </label>
                <input
                  type="url"
                  name="google_review_url"
                  value={formData.google_review_url}
                  onChange={handleChange}
                  className="w-full bg-black/50 border border-yellow-500/30 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50 transition-all"
                  placeholder="https://maps.app.goo.gl/votre-lien"
                />
                <p className="text-gray-500 text-xs mt-2">
                  💡 Trouvez votre lien sur Google Maps → Votre établissement → Partager → Copier le lien
                </p>
              </div>
            )}
          </div>

          {/* Template Hidden - Always ATOM Glass */}
          <input type="hidden" name="template" value="atom-glass" />

          {/* Public Toggle */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-gray-800/50 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Carte publique</h3>
                <p className="text-gray-500 text-sm">Accessible via lien et QR code</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#3B9EFF]"></div>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-gradient-to-r from-[#3B9EFF] to-cyan-400 hover:from-[#2B8EEF] hover:to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#3B9EFF]/20 hover:shadow-xl hover:shadow-[#3B9EFF]/30"
            >
              <Save size={20} />
              <span>{loading ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer la carte')}</span>
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-semibold transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default CardForm;
